/**
 * One tab at a time drains a shared queue.
 *
 * THREE QUEUES LIVE IN INDEXEDDB — the entry outbox, the scan queue and the mapping queue —
 * and all three are shared by every tab on the origin. Each guarded itself with a private
 * `draining` boolean whose comment described exactly the right requirement ("prevents two
 * overlapping drains from posting the same queued item twice") and whose scope was one
 * object, so it held within a tab and not at all between two.
 *
 * MEASURED 2026-09-08 on the booth tenant, two tabs on /entry, airplane mode, then back
 * online:
 *
 *     one queued ENTRY   -> 2 rows, 42.42 @ 09:18:16.899 twice, to the millisecond
 *     one held RECEIPT   -> 2 rows AND 2 stored photographs
 *
 * A visitor's money on the wall twice. tradeshow-2026-09.md calls multi-tab irrelevant —
 * "one visitor, one tab" — and for a VISITOR it is: a stranger's browser opens once and
 * closes. The operator's tethered Android stays open all day, and it is the device the lead
 * beat runs on.
 *
 * WEB LOCKS QUEUES THE SECOND CALLER rather than refusing it, and that is what makes this
 * correct rather than lucky: the waiting tab runs afterwards and finds the queue already
 * empty, instead of racing to post the same item again. `ifAvailable` would have skipped the
 * second drain, which is fine for a duplicate and wrong for the case where the first tab
 * stopped early — still offline, say — and left work behind.
 *
 * FALLS BACK TO RUNNING UNLOCKED where the API is absent: that is the behaviour that
 * shipped, so no browser is worse off than it is today, and refusing to drain at all would
 * strand a visitor's entry to avoid a duplicate. Chrome 69+, Safari 15.4+ — the phones this
 * demo is aimed at.
 */
export async function withDrainLock(name: string, fn: () => Promise<void>): Promise<void> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) return fn();
  await locks.request(name, async () => {
    await fn();
  });
}

/** Lock names. Distinct per queue: a receipt draining must not block an entry draining. */
export const DRAIN_LOCKS = {
  outbox: "easyfarm.outbox.drain",
  scans: "easyfarm.scanQueue.drain",
  mappings: "easyfarm.mappingQueue.drain",
  /** ef-017 — the punch-clock's own queue. Distinct name for the same reason as every
   *  other entry here: two tabs replaying punches must serialize with EACH OTHER, but
   *  must never block (or be blocked by) an unrelated entry/scan/mapping drain. */
  punches: "easyfarm.punch.drain",
  /** ef-040-5 — the time-off outbox's own queue. Same isolation reasoning as `punches`. */
  timeOff: "easyfarm.timeOff.drain",
} as const;
